/**
 * 數據報表 → KPI 統計（平台管理員）
 *
 * 平台自己看的營運統計：一段期間內平台上建立了多少預約單、完成多少、
 * 取消多少、完成率怎麼走。資料來源 GET /api/admin/reports/booking-kpi。
 *
 * 注意：跟 /admin/kpi-report（試營運 KPI 成效報表）不是同一件事。
 * 那支是為了補助案核銷，要比對導入前問卷基準；這支純粹是營運數字。
 *
 * 樣式為此模組獨立的淺色系（白底 + 藍調卡片），刻意與其他 admin 頁面
 * 的莫蘭迪灰不同 —— 報表頁需要高對比才看得清楚數字與圖表。
 */
import React, { useState, useEffect, useCallback } from 'react'
import { Button, DatePicker, Segmented, Spin, Empty, message } from 'antd'
import {
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts'
import api from '../../../utils/api'
import { now as tzNow } from '../../../utils/timezone'

const { RangePicker } = DatePicker

// ── 配色 ────────────────────────────────────────────────────────
const c = {
  ink: '#1e3a5f',
  text: '#334155',
  muted: '#64748b',
  line: '#e2e8f0',
  page: '#f8fafc',
  card: '#ffffff',
  primary: '#2563eb',
  primarySoft: '#93c5fd',
  primaryPale: '#dbeafe',
  success: '#16a34a',
  successPale: '#dcfce7',
  pink: '#ec4899',
  slate: '#94a3b8',
  slatePale: '#f1f5f9'
}

const STATUS_COLORS = [c.primary, c.primarySoft, '#cbd5e1']

// ── 版面 ────────────────────────────────────────────────────────
/**
 * 負 margin 是為了讓報表頁的淺藍底色鋪滿整個 AdminLayout 的 ContentCard
 * （它本身有 24px padding / 16px 圓角）。border-radius 要跟著對齊，
 * 否則底色會蓋掉卡片的圓角，四個角看起來會缺一塊。
 */
const Page = styled.div`
  background: ${c.page};
  margin: -24px;
  padding: 24px 28px 32px;
  border-radius: 16px;

  @media (max-width: 768px) {
    margin: -12px;
    padding: 16px 12px 24px;
    border-radius: 12px;
  }
`

const PageHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`

const Title = styled.h1`
  margin: 0 0 6px;
  font-size: 28px;
  font-weight: 700;
  color: ${c.ink};
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${c.muted};
`

/**
 * 口徑標示。這頁與 /admin/kpi-report 都叫「KPI」也都給預約單量，
 * 但母體定義不同（建單時間 vs 服務日期），不標出來一定會被拿去交叉引用。
 */
const ScopeTag = styled.div`
  margin-top: 6px;
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${c.primaryPale};
  color: ${c.ink};
  font-size: 12px;
  line-height: 1.6;

  b {
    font-weight: 700;
  }
`

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const RangeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${c.card};
  border: 1px solid ${c.line};
  border-radius: 12px;
  padding: 4px 12px 4px 14px;
  color: ${c.muted};
  font-size: 14px;

  .ant-picker {
    border: none;
    box-shadow: none;
    padding-left: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
    .ant-picker {
      flex: 1;
    }
  }
`

const ExportButton = styled(Button)`
  height: 44px;
  border-radius: 12px;
  background: ${c.primary};
  border-color: ${c.primary};
  font-weight: 600;
  padding: 0 20px;

  &:hover,
  &:focus {
    background: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${c.card};
  border: 1px solid ${c.line};
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
`

const StatCard = styled(Card)`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`

const StatLabel = styled.div`
  font-size: 14px;
  color: ${c.muted};
  margin-bottom: 4px;
`

const StatValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: ${c.ink};
  line-height: 1.2;

  small {
    font-size: 14px;
    font-weight: 500;
    color: ${c.muted};
    margin-left: 4px;
  }
`

const StatFoot = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: ${(p) => p.$color || c.muted};
`

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const CardTitle = styled.h2`
  margin: 0 0 18px;
  font-size: 17px;
  font-weight: 700;
  color: ${c.ink};
`

/**
 * 趨勢圖的卡片。grid 會把同一列的兩張卡拉成等高，若圖表固定 300px，
 * 卡片下半部就會空一大塊，所以讓圖表撐滿剩餘高度。
 */
const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
`

const ChartWrap = styled.div`
  width: 100%;
  flex: 1;
  min-height: 300px;
`

const DonutWrap = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
`

/* 圓環中央的總筆數。pointer-events: none 才不會擋住 Pie 的 tooltip。 */
const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  strong {
    font-size: 26px;
    font-weight: 700;
    color: ${c.ink};
    line-height: 1.2;
  }

  span {
    font-size: 12px;
    color: ${c.muted};
  }
`

const DonutLegend = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
    font-size: 14px;
  }

  span.dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-top: 6px;
    flex-shrink: 0;
    background: ${(p) => p.color};
  }

  div.name {
    color: ${c.text};
  }

  div.num {
    color: ${c.muted};
    font-size: 13px;
    margin-top: 2px;
  }
`

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 14px;

  th {
    text-align: right;
    font-weight: 600;
    color: ${c.text};
    background: ${c.slatePale};
    padding: 12px 14px;
    white-space: nowrap;

    &:first-child {
      text-align: left;
      border-radius: 8px 0 0 8px;
    }
    &:last-child {
      border-radius: 0 8px 8px 0;
    }
  }

  td {
    text-align: right;
    padding: 13px 14px;
    color: ${c.text};
    border-bottom: 1px solid ${c.line};
    white-space: nowrap;

    &:first-child {
      text-align: left;
    }
  }

  tr.total td {
    font-weight: 700;
    color: ${c.primary};
    border-bottom: none;
  }
`

/**
 * 計算說明區塊。
 * 注意：這裡不用 antd 的 <Alert>（專案慣例禁用，見 CLAUDE.md），
 * 也不用 common/Hint —— Hint 是玫瑰色調，跟這頁的藍調報表不搭。
 */
const NotePanel = styled(Card)`
  background: #f8fbff;
  border-color: #dbeafe;
`

const NoteTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: ${c.ink};
  margin-bottom: 16px;
`

const NoteBlock = styled.div`
  margin-bottom: 16px;

  h4 {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: ${c.primary};
  }

  p {
    margin: 0 0 4px;
    font-size: 13px;
    line-height: 1.7;
    color: ${c.text};
  }
`

const NoteFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid #dbeafe;
  font-size: 12px;
  color: ${c.muted};
`

const CenterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
`

// ── 工具 ────────────────────────────────────────────────────────
const num = (v) => Number(v || 0).toLocaleString('zh-TW')
const pct = (v) => `${Number(v || 0).toFixed(2)}%`

/** 2026-06 → 顯示用標籤（趨勢圖 X 軸與表格都用同一份，避免兩邊格式不一致） */
const monthLabel = (m) => m

const KpiStatsPage = () => {
  const [range, setRange] = useState([
    tzNow().subtract(2, 'month').startOf('month'),
    tzNow().endOf('month')
  ])
  // 'service' = 依服務日期（與試營運 KPI 成效報表同口徑，預設）
  // 'created' = 依建單時間（看這段期間接了多少單）
  const [basis, setBasis] = useState('service')
  const [excludeSimulation, setExcludeSimulation] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [data, setData] = useState(null)

  const fetchData = useCallback(async () => {
    if (!range?.[0] || !range?.[1]) return
    setLoading(true)
    try {
      const res = await api.get('/api/admin/reports/booking-kpi', {
        params: {
          startDate: range[0].format('YYYY-MM-DD'),
          endDate: range[1].format('YYYY-MM-DD'),
          basis,
          ...(excludeSimulation ? { excludeSimulation: 'true' } : {})
        }
      })
      setData(res.data?.data || null)
    } catch (error) {
      console.error('取得 KPI 統計失敗:', error)
      message.error(
        error.response?.data?.message || '取得數據報表失敗，請稍後再試'
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [range, basis, excludeSimulation])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await api.get('/api/admin/reports/booking-kpi/export', {
        params: {
          startDate: range[0].format('YYYY-MM-DD'),
          endDate: range[1].format('YYYY-MM-DD'),
          basis,
          ...(excludeSimulation ? { excludeSimulation: 'true' } : {})
        },
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `KPI統計_${range[0].format('YYYYMMDD')}_${range[1].format(
        'YYYYMMDD'
      )}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('匯出報表失敗:', error)
      message.error('匯出失敗，請稍後再試')
    } finally {
      setExporting(false)
    }
  }

  const s = data?.summary
  const cmp = data?.comparison
  const months = data?.months || []
  const periodText = data
    ? `${data.period.startDate} - ${data.period.endDate}`
    : ''

  const donutData = s
    ? [
        { name: '已完成', value: s.completed, rate: s.completionRate },
        { name: '已取消', value: s.canceled, rate: s.canceledRate },
        { name: '其他狀態', value: s.other, rate: s.otherRate }
      ]
    : []

  const chartData = months.map((m) => ({
    month: monthLabel(m.month),
    預約單量: m.total,
    已完成: m.completed,
    完成率: m.completionRate
  }))

  const renderCompare = () => {
    if (!cmp || cmp.totalChangeRate === null) {
      return <StatFoot>較前期 —</StatFoot>
    }
    const up = cmp.totalChangeRate >= 0
    return (
      <StatFoot $color={up ? c.success : c.pink}>
        {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} 較前期{' '}
        {Math.abs(cmp.totalChangeRate).toFixed(2)}%
      </StatFoot>
    )
  }

  return (
    <Page>
      <PageHead>
        {/* <div>
          <Title>KPI 統計</Title>
          <Subtitle>掌握平台營運成效，創造更美好的美業生態</Subtitle>
          <ScopeTag>
            {basis === 'service' ? (
              <>
                統計口徑：依<b>服務日期</b>（與試營運 KPI 成效報表同口徑，數字應一致）
              </>
            ) : (
              <>
                統計口徑：依<b>建單時間</b>（這段期間平台接到多少單，與 KPI 成效報表不同）
              </>
            )}
            　·　店家範圍：<b>{excludeSimulation ? '只計真實營運' : '含展示店家'}</b>
          </ScopeTag>
        </div> */}
        <HeadActions>
          <RangeBox>
            <CalendarOutlined />
            <span>統計期間</span>
            <RangePicker
              value={range}
              onChange={(v) => v && setRange(v)}
              allowClear={false}
              format='YYYY/MM/DD'
              inputReadOnly
            />
          </RangeBox>
          <Segmented
            value={basis}
            onChange={setBasis}
            options={[
              { value: 'service', label: '依服務日期' },
              { value: 'created', label: '依建單時間' }
            ]}
          />
          {/* <Segmented
            value={excludeSimulation ? 'real' : 'all'}
            onChange={(v) => setExcludeSimulation(v === 'real')}
            options={[
              { value: 'all', label: '含展示店家' },
              { value: 'real', label: '只計真實營運' }
            ]}
          /> */}
          <ExportButton
            type='primary'
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={!data}
            onClick={handleExport}
          >
            匯出報表
          </ExportButton>
        </HeadActions>
      </PageHead>

      {loading && !data ? (
        <Card>
          <CenterBox>
            <Spin size='large' tip='載入中…' />
          </CenterBox>
        </Card>
      ) : !data ? (
        <Card>
          <CenterBox>
            <Empty description='目前沒有可顯示的資料' />
          </CenterBox>
        </Card>
      ) : (
        <Spin spinning={loading}>
          <StatGrid>
            <StatCard>
              <StatIcon $bg={c.primaryPale} $color={c.primary}>
                <FileTextOutlined />
              </StatIcon>
              <div>
                <StatLabel>預約單總量</StatLabel>
                <StatValue>
                  {num(s.total)}
                  <small>筆</small>
                </StatValue>
                {renderCompare()}
              </div>
            </StatCard>

            <StatCard>
              <StatIcon $bg={c.successPale} $color={c.success}>
                <CheckCircleOutlined />
              </StatIcon>
              <div>
                <StatLabel>已完成預約單</StatLabel>
                <StatValue>
                  {num(s.completed)}
                  <small>筆</small>
                </StatValue>
                <StatFoot>
                  完成率{' '}
                  <span style={{ color: c.success, fontWeight: 600 }}>
                    {pct(s.completionRate)}
                  </span>
                </StatFoot>
              </div>
            </StatCard>

            <StatCard>
              <StatIcon $bg={c.slatePale} $color={c.muted}>
                <ClockCircleOutlined />
              </StatIcon>
              <div>
                <StatLabel>已取消預約單</StatLabel>
                <StatValue>
                  {num(s.canceled)}
                  <small>筆</small>
                </StatValue>
                <StatFoot>占比 {pct(s.canceledRate)}</StatFoot>
              </div>
            </StatCard>

            <StatCard>
              <StatIcon $bg={c.slatePale} $color={c.muted}>
                <MinusCircleOutlined />
              </StatIcon>
              <div>
                <StatLabel>其他狀態</StatLabel>
                <StatValue>
                  {num(s.other)}
                  <small>筆</small>
                </StatValue>
                <StatFoot>占比 {pct(s.otherRate)}</StatFoot>
              </div>
            </StatCard>
          </StatGrid>

          <ChartRow>
            <ChartCard>
              <CardTitle>預約單量與完成率趨勢</CardTitle>
              <ChartWrap>
                <ResponsiveContainer width='100%' height='100%'>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                  >
                    <CartesianGrid stroke={c.line} vertical={false} />
                    <XAxis
                      dataKey='month'
                      tick={{ fontSize: 12, fill: c.muted }}
                      axisLine={{ stroke: c.line }}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId='left'
                      tick={{ fontSize: 12, fill: c.muted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId='right'
                      orientation='right'
                      domain={[0, 100]}
                      unit='%'
                      tick={{ fontSize: 12, fill: c.muted }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === '完成率'
                          ? [`${Number(value).toFixed(2)}%`, name]
                          : [`${num(value)} 筆`, name]
                      }
                      contentStyle={{
                        borderRadius: 10,
                        border: `1px solid ${c.line}`,
                        fontSize: 13
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 13, paddingTop: 4 }}
                      iconType='circle'
                    />
                    <Bar
                      yAxisId='left'
                      dataKey='預約單量'
                      fill={c.primarySoft}
                      radius={[4, 4, 0, 0]}
                      barSize={38}
                    />
                    <Bar
                      yAxisId='left'
                      dataKey='已完成'
                      fill={c.primary}
                      radius={[4, 4, 0, 0]}
                      barSize={38}
                    />
                    <Line
                      yAxisId='right'
                      type='monotone'
                      dataKey='完成率'
                      stroke={c.pink}
                      strokeWidth={2}
                      dot={{ r: 4, fill: c.pink }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartWrap>
            </ChartCard>

            <Card>
              <CardTitle>預約單狀態分布（{periodText}）</CardTitle>
              <DonutWrap>
                <ResponsiveContainer width='100%' height='100%'>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey='value'
                      nameKey='name'
                      innerRadius={62}
                      outerRadius={92}
                      paddingAngle={1}
                      stroke='none'
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={entry.name} fill={STATUS_COLORS[i]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${num(value)} 筆`, name]}
                      contentStyle={{
                        borderRadius: 10,
                        border: `1px solid ${c.line}`,
                        fontSize: 13
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <DonutCenter>
                  <strong>{num(s.total)}</strong>
                  <span>總筆數</span>
                </DonutCenter>
              </DonutWrap>
              <DonutLegend>
                {donutData.map((d, i) => (
                  <li key={d.name}>
                    <span
                      className='dot'
                      style={{ background: STATUS_COLORS[i] }}
                    />
                    <div>
                      <div className='name'>{d.name}</div>
                      <div className='num'>
                        {num(d.value)} 筆（{pct(d.rate)}）
                      </div>
                    </div>
                  </li>
                ))}
              </DonutLegend>
            </Card>
          </ChartRow>

          <BottomRow>
            <Card>
              <CardTitle>月別預約數據明細</CardTitle>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>月份</th>
                      <th>預約單量</th>
                      <th>已完成</th>
                      <th>已取消</th>
                      <th>其他狀態</th>
                      <th>完成率</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m) => (
                      <tr key={m.month}>
                        <td>{m.month}</td>
                        <td>{num(m.total)}</td>
                        <td>{num(m.completed)}</td>
                        <td>{num(m.canceled)}</td>
                        <td>{num(m.other)}</td>
                        <td>{pct(m.completionRate)}</td>
                      </tr>
                    ))}
                    <tr className='total'>
                      <td>合計</td>
                      <td>{num(s.total)}</td>
                      <td>{num(s.completed)}</td>
                      <td>{num(s.canceled)}</td>
                      <td>{num(s.other)}</td>
                      <td>{pct(s.completionRate)}</td>
                    </tr>
                  </tbody>
                </Table>
              </TableScroll>
            </Card>

            <NotePanel>
              <NoteTitle>
                <InfoCircleOutlined style={{ color: c.primary }} />
                計算說明
              </NoteTitle>
              <NoteBlock>
                <h4>預約單總量</h4>
                <p>
                  統計期間內的所有預約單（bookings
                  資料表），含已完成、已取消、待確認、已確認、未到店等各種狀態。
                  以
                  {basis === 'service'
                    ? '「服務日期」'
                    : '「建立時間」（台灣時間）'}
                  認列，已刪除的預約單不計入。
                </p>
              </NoteBlock>
              <NoteBlock>
                <h4>預約完成率</h4>
                <p>= 已完成預約單 ÷ 預約單總量 × 100%</p>
                <p>
                  = {num(s.completed)} ÷ {num(s.total)} × 100% ={' '}
                  {pct(s.completionRate)}
                </p>
              </NoteBlock>
              <NoteBlock>
                <h4>其他狀態</h4>
                <p>
                  包含待確認（pending）、已確認尚未完成（confirmed）、未到店（no_show）。
                </p>
              </NoteBlock>
              <NoteBlock>
                <h4>較前期</h4>
                <p>
                  與前一段等長期間比較
                  {cmp?.period
                    ? `（${cmp.period.startDate} - ${cmp.period.endDate}）`
                    : ''}
                  。前期無資料時顯示「—」。
                </p>
              </NoteBlock>
              <NoteFoot>
                <ClockCircleOutlined />
                資料更新時間：{data.updatedAt}
              </NoteFoot>
            </NotePanel>
          </BottomRow>
        </Spin>
      )}
    </Page>
  )
}

export default KpiStatsPage
